import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Form, Formik } from "formik";
import { csvColumnMappingSchema } from "formValidations/csvColumnMappingSchema";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useGetDateFormatsQuery } from "store/apis/dateformat";
import { usePopulateCSVTransactionsMutation } from "store/apis/transactions";
import { CSVParsedResponse, CSVResponse } from "types/Models/reconciliation";
import { BackendError } from "types/utils/error";

export const CSVColumnMapping = ({
  uploadedCsvData,
  setCurrentStep,
  setCsvTransactions,
  onBack,
}: {
  uploadedCsvData?: CSVParsedResponse;
  setCurrentStep: (newState: React.SetStateAction<number>) => void;
  setCsvTransactions: (
    newState: React.SetStateAction<CSVResponse[] | undefined>
  ) => void;
  onBack: () => void;
}) => {
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { data: date_formats = [], ...dateFormatsState } =
    useGetDateFormatsQuery();
  const [populateCSVTransactions] = usePopulateCSVTransactionsMutation();

  const {
    column_headers,
    labels_in_csv,
    csv,
    entity_transaction_labels,
    column_mapping,
  } = uploadedCsvData || {};

  const columnOptions = (column_headers ?? labels_in_csv) || [];

  const options = columnOptions.map((value) => ({
    label: value,
    value: value,
  }));

  const { bank_account } = csv || {};
  const { account } = bank_account || {};
  const { mask, nickname } = account || {};
  const source = `${nickname} •••• ${mask}`;
  const isCSV = csv?.csv_name?.endsWith(".csv") ?? true;

  const isSVBBank = entity_transaction_labels?.some(
    (value) =>
      value.toLowerCase().includes("credit") ||
      value.toLowerCase().includes("debit")
  )!;

  const onSubmit = async (values: {
    date_field: string;
    description_field: string;
    merchant_field: string;
    amount_field: string;
    credit_field: string;
    debit_field: string;
    memo_field: string;
    date_format: string;
    bank_csv_id: string;
    category_field: string;
    isSVBBank: boolean;
    source: string;
  }) => {
    const { isSVBBank, source, ...restPayload } = values;
    try {
      const csvTransactions = await populateCSVTransactions({
        entityId,
        groupId,
        ...restPayload,
      }).unwrap();
      setCsvTransactions(csvTransactions);
      successToast({ message: "Document parsed!" });
      setCurrentStep(3);
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <>
      <Formik
        initialValues={{
          date_field: column_mapping?.date_field || "",
          description_field: column_mapping?.description_field || "",
          merchant_field: column_mapping?.merchant_field || "",
          amount_field: column_mapping?.amount_field || "",
          credit_field: column_mapping?.credit_field || "",
          debit_field: column_mapping?.debit_field || "",
          memo_field: column_mapping?.memo_field || "",
          date_format: column_mapping?.date_format || "",
          bank_csv_id: csv?.uuid!,
          category_field: column_mapping?.category_field || "",
          isSVBBank,
          source,
        }}
        onSubmit={onSubmit}
        validationSchema={csvColumnMappingSchema}
        validateOnChange
        enableReinitialize
      >
        {({ submitForm, values, isSubmitting }) => {
          const { isSVBBank } = values;
          const toOption = (fieldValue: string) =>
            fieldValue ? { label: fieldValue, value: fieldValue } : null;

          return (
            <Form className="t-m-0 t-w-full t-flex t-flex-col t-flex-1 t-min-h-0">
              <Modal.Body className="t-grid t-grid-cols-2 t-gap-5 t-w-full t-flex-1 t-min-h-0">
                <Combobox
                  label="Date"
                  required
                  name="date_field"
                  withForm
                  placeholder="Select property"
                  menuPortalTarget={document.getElementById("root")}
                  options={options}
                  value={toOption(values.date_field)}
                  block
                />
                <Combobox
                  isDisabled={dateFormatsState.isLoading}
                  label="Date format"
                  required
                  name="date_format"
                  withForm
                  placeholder="Select date format"
                  menuPortalTarget={document.getElementById("root")}
                  options={date_formats.map((format) => ({
                    label: (
                      <div className="t-flex t-justify-between t-items-center t-gap-2 t-w-full">
                        <div>{format.format}</div>
                        <div className="t-text-neutral-40 t-text-body-sm-italic">
                          Eg: {format.example}
                        </div>
                      </div>
                    ),
                    value: format.format,
                  }))}
                  value={toOption(values.date_format)}
                  block
                />
                <Combobox
                  required
                  label="Description"
                  menuPortalTarget={document.getElementById("root")}
                  name="description_field"
                  withForm
                  placeholder="Select property"
                  options={options}
                  value={toOption(values.description_field)}
                  block
                />
                {isSVBBank && (
                  <>
                    <Combobox
                      required
                      label="Credit"
                      menuPortalTarget={document.getElementById("root")}
                      name="credit_field"
                      withForm
                      placeholder="Select property"
                      options={options}
                      value={toOption(values.credit_field)}
                      block
                    />
                    <Combobox
                      required
                      label="Debit"
                      menuPortalTarget={document.getElementById("root")}
                      name="debit_field"
                      withForm
                      placeholder="Select property"
                      options={options}
                      value={toOption(values.debit_field)}
                      block
                    />
                  </>
                )}
                {!isSVBBank && (
                  <Combobox
                    required
                    label="Amount"
                    menuPortalTarget={document.getElementById("root")}
                    name="amount_field"
                    withForm
                    placeholder="Select property"
                    options={options}
                    value={toOption(values.amount_field)}
                    block
                  />
                )}
                <Combobox
                  label="Vendor"
                  menuPortalTarget={document.getElementById("root")}
                  name="merchant_field"
                  withForm
                  placeholder="Select property"
                  options={options}
                  value={toOption(values.merchant_field)}
                  block
                />
                <Combobox
                  label="Category"
                  menuPortalTarget={document.getElementById("root")}
                  name="category_field"
                  withForm
                  placeholder={
                    isCSV ? "Not available for CSV" : "Select property"
                  }
                  options={isCSV ? [] : options}
                  value={isCSV ? null : toOption(values.category_field)}
                  isDisabled={isCSV}
                  block
                />
                <Combobox
                  label="Memo"
                  menuPortalTarget={document.getElementById("root")}
                  name="memo_field"
                  withForm
                  placeholder="Select property"
                  options={options}
                  value={toOption(values.memo_field)}
                  block
                />
                <TextInput label="Source" disabled name="source" />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Button onClick={onBack} type="reset" disabled={isSubmitting}>
                  Back
                </Button>
                <Button
                  customType="primary"
                  type="submit"
                  onClick={submitForm}
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  Next
                </Button>
              </Modal.FooterButtonGroup>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
